import React, { useState, useMemo, useEffect, useRef } from 'react'
import { IconButton, Typography, Chip, Box } from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import dayjs from 'dayjs'
import { useRouteQuery, useTranslator } from '@/hooks'
import { AttendanceStatusEnum, BalanceStatusEnum } from '@/@data'

interface FillterMonthLogProps {
  selectedDate?: Date
  onDateChange?: (dateString: string) => void
  status?: string
  className?: string
  isShowHistory?: boolean
  onShowHistory?: () => void
  showToday?: boolean
  data?: any
  isShowWorkingRate?: boolean
  isShowSettingDay?: boolean
}

const FillterMonthLog: React.FC<FillterMonthLogProps> = ({
  selectedDate,
  onDateChange,
  status = '',
  className = '',
  isShowHistory = false,
  onShowHistory,
  showToday = true,
  data = {},
  isShowWorkingRate = false
}) => {
  const { __ } = useTranslator()
  const { updateQuery, searchQuery } = useRouteQuery()
  const monthFromUrl = searchQuery.get('month')

  const getInitialDate = () => {
    if (monthFromUrl) {
      return dayjs(monthFromUrl).toDate()
    }

    if (selectedDate) {
      return selectedDate
    }

    return new Date()
  }

  const [currentDate, setCurrentDate] = useState<Date>(getInitialDate())
  const [currentStatus, setCurrentStatus] = useState<string>(status || '')
  const updateQueryRef = useRef(updateQuery)

  updateQueryRef.current = updateQuery

  useEffect(() => {
    setCurrentStatus(status || '')
  }, [status, data])

  useEffect(() => {
    if (monthFromUrl) {
      const newDate = dayjs(monthFromUrl).toDate()

      setCurrentDate(newDate)
    }
  }, [monthFromUrl])

  const currentMonth = useMemo(() => {
    return dayjs(currentDate).format('MMMM YYYY')
  }, [currentDate])

  // const dateRange = useMemo(() => {
  //   const start = dayjs(currentDate).startOf('month')
  //   const end = dayjs(currentDate).endOf('month')

  //   return {
  //     start: start.format('DD/MM/YYYY'),
  //     end: end.format('DD/MM/YYYY')
  //   }
  // }, [currentDate])

  useEffect(() => {
    if (!searchQuery.get('month')) {
      const today = dayjs().startOf('month').format('YYYY-MM-DD')

      updateQueryRef.current({ month: today })
    }
  }, [searchQuery.get('month')])

  const isCurrentMonth = useMemo(() => {
    return dayjs(currentDate).isSame(dayjs(), 'month')
  }, [currentDate])

  const handlePreviousMonth = () => {
    const newDate = dayjs(currentDate).subtract(1, 'month')

    setCurrentDate(newDate.toDate())

    onDateChange?.(newDate.startOf('month').format('YYYY-MM-DD'))
  }

  const handleNextMonth = () => {
    const newDate = dayjs(currentDate).add(1, 'month')

    setCurrentDate(newDate.toDate())

    onDateChange?.(newDate.startOf('month').format('YYYY-MM-DD'))
  }

  const handleToday = () => {
    const today = dayjs()

    setCurrentDate(today.toDate())

    onDateChange?.(today.startOf('month').format('YYYY-MM-DD'))
  }

  return (
    <Box className={`flex items-center justify-between bg-white ${className}`}>
      <div className='flex items-center gap-3'>
        <Box className='flex items-center gap-3'>
          <IconButton onClick={handlePreviousMonth} size='small' className='text-gray-600 hover:text-gray-800'>
            <i className='tabler-chevron-left' />
          </IconButton>

          <IconButton
            onClick={handleNextMonth}
            size='small'
            className='text-gray-600 hover:text-gray-800'
            disabled={isCurrentMonth}
          >
            <i className='tabler-chevron-right' />
          </IconButton>

          <Typography variant='h6' className='text-gray-800 font-medium text-[24px]'>
            {currentMonth}
          </Typography>
        </Box>

        <div className='flex items-center gap-3'>
          {currentStatus && (
            <Chip
              label={currentStatus}
              size='small'
              className='capitalize rounded-full p-4'
              classes={
                currentStatus === AttendanceStatusEnum.Unreconciled
                  ? {
                      root: 'bg-red-50 text-red-600 border-red-200',
                      label: 'text-red-600'
                    }
                  : currentStatus === AttendanceStatusEnum.Reconciled
                    ? {
                        root: 'bg-green-50 text-green-600 border-green-200',
                        label: 'text-green-600'
                      }
                    : currentStatus === AttendanceStatusEnum.Checking
                      ? {
                          root: 'bg-blue-50 text-blue-600 border-blue-200',
                          label: 'text-blue-600'
                        }
                      : currentStatus === BalanceStatusEnum.Paid
                        ? {
                            root: 'bg-[#CCF2DD] text-[#28C76F] border-green-200',
                            label: 'text-green-600'
                          }
                        : // : currentStatus === BalanceStatusEnum.Estimate_Balance
                          //   ? {
                          //       root: 'bg-[#CFD5FC] text-[#3551F2] border-blue-200',
                          //       label: 'text-blue-600'
                          //     }
                          //   : {
                          //       root: 'bg-gray-50 text-gray-600 border-gray-200',
                          {
                            root: 'bg-gray-50 text-gray-600 border-gray-200',
                            label: 'text-gray-600'
                          }
              }
              variant='tonal'
            />
          )}

          {currentStatus === AttendanceStatusEnum.Unreconciled && isShowWorkingRate && (
            <div className='flex items-center gap-2 bg-[#97939e] py-1 px-3 rounded-full'>
              <i className='tabler-calendar-event text-[#fff]' style={{ fontSize: 16 }} />
              <span className='text-[14px] font-medium text-[#fff]'>
                {__('Assumption Working Rate')} {Number(data?.company?.working_rate)}%
              </span>
            </div>
          )}
        </div>
      </div>

      <Box className='flex items-center gap-3'>
        {/* {!isShowSettingDay && (
        <Button variant='outlined' size='small' className='border-blue-500 text-blue-600 hover:border-blue-600'>
          <span className='text-[15px] font-medium'>
            {dateRange.start} - {dateRange.end}
          </span>
        </Button>
        )}

        {isShowSettingDay && (
        <Button variant='outlined' size='small' className='border-blue-500 text-blue-600 hover:border-blue-600'>
          <span className='text-[15px] font-medium'>
            {dayjs(data?.attendance?.startDate).format('DD/MM/YYYY')} - {dayjs(data?.attendance?.endDate).format('DD/MM/YYYY')}
          </span>
        </Button>
        )} */}

        {showToday && (
          <Button variant='tonal' size='small' color='primary' onClick={handleToday} disabled={isCurrentMonth}>
            {__('Current Month')}
          </Button>
        )}

        {isShowHistory && (
          <Button
            variant='tonal'
            size='small'
            color='primary'
            startIcon={<i className='tabler-history' />}
            className='bg-[#00bad129] text-[#00bad1]'
            onClick={onShowHistory}
          >
            {__('History')}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default FillterMonthLog
